<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { useStore } from "vuex"
import { showSnackbar } from "@/helper"
import { getBrowserInfo } from "@/helper"
import { AdyenCheckout } from "@adyen/adyen-web"
import { ref, toRaw, watch, computed, onMounted } from "vue"
import { useCart } from "@/app/Checkout/composables/useCart"
import { handleAdditionalDetails } from "@/composables/useAdyen"
import { usePayment, paymentManager } from "@/composables/usePayment"
import { useCheckout } from "@/app/Checkout/composables/useCheckout"

const store = useStore()
const { cart } = useCart()
const googlePayContainer = ref(null)
const { adyenConfig, proceedToPayment } = usePayment()
const { activePaymentMethod } = useCheckout()
const baseRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
}
const tokenizationSpecification = {
  type: 'PAYMENT_GATEWAY',
  parameters: {
    gateway: 'adyen',
    gatewayMerchantId: adyenConfig.value?.merchant_account,
  },
}
const allowedCardNetworks = computed(() => adyenConfig.value?.allowedCardNetworks?.[activePaymentMethod.value] || [])
const allowedCardAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"]
const baseCardPaymentMethod = {
  type: 'CARD',
  parameters: {
    allowedAuthMethods: allowedCardAuthMethods,
    allowedCardNetworks: allowedCardNetworks.value,
  },
}

const cardPaymentMethod = Object.assign({ tokenizationSpecification }, baseCardPaymentMethod)

watch(allowedCardNetworks, (brands) => {
  baseCardPaymentMethod.parameters.allowedCardNetworks = brands
})

let paymentsClient = null

const handle3DS2Authentication = async (action) => {
  const adyenCheckout = await AdyenCheckout({
    locale: adyenConfig.value?.locale,
    environment: adyenConfig.value?.env,
    clientKey: adyenConfig.value?.client_key,
    countryCode: adyenConfig.value?.countryCode,
    onAdditionalDetails: handleAdditionalDetails,
  });

  adyenCheckout.createFromAction(action).mount("#googlepay-container");
}

const init = async () => {
  try {
    const paymentDataRequest = Object.assign({}, baseRequest);
    paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];

    paymentDataRequest.transactionInfo = {
      totalPriceStatus: 'FINAL',
      currencyCode: cart.value?.currency,
      totalPrice: cart.value?.total_price_numeric,
      countryCode: adyenConfig.value?.countryCode,
    };

    paymentDataRequest.merchantInfo = {
      merchantName: 'Niche Beauty',
      merchantId: 'BCR2DN4T2OJOX5D2',
    };

    const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest)

    const response = await proceedToPayment({ googlepay_token: paymentData.paymentMethodData.tokenizationData.token, browser_info: getBrowserInfo() })

    if (response.url?.type === 'threeDS2') {
      await handle3DS2Authentication(response.url)
    } else if (response?.result?.resultCode === 'Authorised' && response?.url) {
      window.location.href = response.url
    }
  } catch(error) {
    console.log(error)
  }
}

onMounted(async () => {
  loadGooglePayScript()
})

const initGooglePay = () => {
  paymentsClient = new window.google.payments.api.PaymentsClient({ environment: adyenConfig.value?.env === 'test' ? 'TEST' : 'PRODUCTION' })

  const isReadyToPayRequest = Object.assign({}, baseRequest)

  isReadyToPayRequest.allowedPaymentMethods = [baseCardPaymentMethod]

  paymentManager.register({ init })
}

const loadGooglePayScript = () => {
  const script = document.createElement('script')
  script.src = 'https://pay.google.com/gp/p/js/pay.js'
  document.head.append(script)

  script.onload = initGooglePay
}
</script>
